import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { TrendingUp, TrendingDown, DollarSign, BarChart3, AlertCircle, Users, Truck, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (n) =>
  n === null || n === undefined ? "—" : `$${Number(n).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;
const pct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const Finanzas = () => {
  const [desde, setDesde] = useState(firstOfMonth());
  const [hasta, setHasta] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reportes/financiero`, { params: { desde, hasta } });
      setData(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el reporte");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const cards = data
    ? [
        { label: "Ingresos del periodo", value: fmt(data.total_ingresos), icon: TrendingUp, color: "text-emerald-700", bg: "bg-emerald-50" },
        { label: "Egresos del periodo", value: fmt(data.total_egresos), icon: TrendingDown, color: "text-amber-700", bg: "bg-amber-50" },
        { label: "Utilidad", value: fmt(data.utilidad_periodo), icon: DollarSign, color: data.utilidad_periodo >= 0 ? "text-emerald-800" : "text-red-700", bg: data.utilidad_periodo >= 0 ? "bg-emerald-50" : "bg-red-50" },
        { label: "Margen", value: pct(data.margen_periodo_pct), icon: BarChart3, color: "text-stone-700", bg: "bg-stone-50" },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="finanzas-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-stone-900">Finanzas</h1>
          <p className="text-stone-500 text-sm mt-1">Reporte de utilidad por periodo, eventos y beneficiarios.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="finanzas-desde" />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="finanzas-hasta" />
          </div>
          <Button onClick={fetchData} disabled={loading} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-finanzas-actualizar">
            Actualizar
          </Button>
        </div>
      </div>

      {loading && <div className="text-stone-500 text-sm">Cargando...</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            {cards.map((c) => (
              <Card key={c.label} className={`border-stone-200 min-w-0 ${c.bg}`}>
                <CardContent className="p-3 sm:p-4 min-w-0">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <c.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${c.color}`} />
                  </div>
                  <p className="text-[11px] sm:text-xs text-stone-600 truncate">{c.label}</p>
                  <p className={`text-base sm:text-xl font-semibold break-anywhere ${c.color}`}>{c.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {data.num_egresos_pendientes > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-3 flex items-center gap-2 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4" />
                {data.num_egresos_pendientes} egreso{data.num_egresos_pendientes !== 1 ? "s" : ""} con estatus pendiente (no incluidos en el total).
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-stone-200">
              <CardContent className="p-5">
                <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-amber-700" /> Egresos por tipo
                </h3>
                {(data.breakdown_egresos_tipo || []).length === 0 ? (
                  <p className="text-stone-500 text-sm">Sin egresos registrados en el periodo.</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.breakdown_egresos_tipo.map((r) => (
                      <div key={r.tipo} className="flex justify-between text-sm border-b border-stone-100 py-1.5">
                        <span className="text-stone-700 capitalize">{r.tipo}</span>
                        <span className="font-medium text-stone-900">{fmt(r.monto)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-stone-200">
              <CardContent className="p-5">
                <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-stone-700" /> Egresos por categoria
                </h3>
                {(data.breakdown_egresos_categoria || []).length === 0 ? (
                  <p className="text-stone-500 text-sm">Sin datos.</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.breakdown_egresos_categoria.map((r) => (
                      <div key={r.categoria} className="flex justify-between text-sm border-b border-stone-100 py-1.5">
                        <span className="text-stone-700 capitalize">{r.categoria}</span>
                        <span className="font-medium text-stone-900">{fmt(r.monto)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="border-stone-200">
            <CardContent className="p-5">
              <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-stone-700" /> Top beneficiarios (personal / proveedores)
              </h3>
              {(data.top_beneficiarios || []).length === 0 ? (
                <p className="text-stone-500 text-sm">Sin pagos a beneficiarios registrados.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-stone-500 text-xs border-b border-stone-200">
                        <th className="py-2">Beneficiario</th>
                        <th className="py-2">Tipo</th>
                        <th className="py-2 text-right">Transacciones</th>
                        <th className="py-2 text-right">Monto total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_beneficiarios.map((b) => (
                        <tr key={b.beneficiario_id} className="border-b border-stone-100">
                          <td className="py-2 text-stone-800 font-medium">{b.nombre}</td>
                          <td className="py-2 text-stone-600 capitalize">{b.tipo}</td>
                          <td className="py-2 text-right text-stone-600">{b.transacciones}</td>
                          <td className="py-2 text-right font-semibold text-stone-900">{fmt(b.monto)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardContent className="p-5">
              <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-stone-700" /> Utilidad por evento ({data.num_eventos})
              </h3>
              {(data.eventos || []).length === 0 ? (
                <p className="text-stone-500 text-sm">Sin eventos en el periodo.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-stone-500 text-xs border-b border-stone-200">
                        <th className="py-2">Evento</th>
                        <th className="py-2">Fecha</th>
                        <th className="py-2 text-right">Vendido</th>
                        <th className="py-2 text-right">Pagado</th>
                        <th className="py-2 text-right">Costo real</th>
                        <th className="py-2 text-right">Utilidad</th>
                        <th className="py-2 text-right">Margen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.eventos.map((ev) => (
                        <tr key={ev.evento_id} className="border-b border-stone-100 hover:bg-stone-50">
                          <td className="py-2">
                            <Link to={`/panel/eventos/${ev.evento_id}`} className="text-emerald-800 hover:underline" data-testid={`finanzas-evento-${ev.evento_id}`}>
                              {ev.nombre || ev.evento_id}
                            </Link>
                            <p className="text-xs text-stone-500 capitalize">{ev.tipo}</p>
                          </td>
                          <td className="py-2 text-stone-600 text-xs">{ev.fecha}</td>
                          <td className="py-2 text-right text-stone-700">{fmt(ev.total_vendido)}</td>
                          <td className="py-2 text-right text-emerald-700">{fmt(ev.total_pagado)}</td>
                          <td className="py-2 text-right text-amber-700">{fmt(ev.costo_real)}</td>
                          <td className={`py-2 text-right font-semibold ${ev.utilidad >= 0 ? "text-emerald-800" : "text-red-700"}`}>
                            {fmt(ev.utilidad)}
                          </td>
                          <td className="py-2 text-right text-stone-600">{pct(ev.margen_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Finanzas;
